"use client";

/**
 * Records clicks on CTA banners: the FinalCTASection banner on ~40 pages, the
 * buttons editors place inside blog articles, and server-rendered button groups
 * marked with data-cta-section (the home page hero).
 *
 * One document-level listener rather than an onClick per banner, for the same reasons
 * as useProductLinkTracking: FinalCTASection stays a server component (no hydration
 * on 40 pages), every banner added later is covered, and the editor's buttons -- which
 * arrive as raw post HTML -- cannot take a React handler at all.
 *
 * What gets recorded is decided by bannerClickEvent() in src/lib/ctaBanner.ts.
 */

import { useEffect } from "react";
import { bannerClickEvent } from "@/lib/ctaBanner";
import { trackCtaClick } from "@/utils/ctaTracking";

export function useCtaBannerTracking(): void {
  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (e.defaultPrevented) return;

      const el = (e.target as Element | null)?.closest?.("a[href]") as HTMLAnchorElement | null;
      if (!el) return;

      const banner = el.closest("[data-cta-banner]");
      const event = bannerClickEvent({
        href: el.getAttribute("href") || "",
        text: el.textContent || "",
        bannerVariant: banner ? banner.getAttribute("data-cta-banner") ?? "custom" : null,
        inArticleButton: !!el.closest("[data-cta-button]"),
        section: el.closest("[data-cta-section]")?.getAttribute("data-cta-section") ?? null,
      });
      if (!event) return;

      trackCtaClick(event.destination, event.label, undefined, {
        section: event.section,
        location: event.location,
      });
    };

    document.addEventListener("click", onClick, true);
    return () => document.removeEventListener("click", onClick, true);
  }, []);
}
